import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CircleAlert,
  Database,
  Download,
  HeartPulse,
  Plug,
  RefreshCw,
  ShieldCheck,
  Triangle,
} from "lucide-react";
import type { ReactNode } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { AnimatedNumber } from "@/components/ui/AnimatedNumber";
import { LevelBadge } from "@/components/logs/LevelBadge";
import { useAuth } from "@/hooks/useAuth";
import { useOverviewData } from "@/hooks/useOverviewData";
import { useSystemHealth } from "@/hooks/useSystemHealth";
import { formatCount } from "@/lib/format";
import { t, dateLocale } from "@/lib/i18n";
import type { LogEntry } from "@/lib/types";

export function OverviewPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error, refresh } = useOverviewData(!!token);
  const { license, update } = useSystemHealth();
  const freshnessText = formatFreshness(data?.lastUpdated ?? null);

  return (
    <div>
      <PageHeader
        title={t("Übersicht")}
        description={t("Live-Stats der letzten 24 Stunden")}
        actions={
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-muted disabled:opacity-60"
          >
            <RefreshCw className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} />
            {loading ? t("Aktualisiere…") : t("Aktualisieren")}
          </button>
        }
      />

      <div className="flex flex-col gap-4 p-6">
        {error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        ) : null}

        <div className="text-xs text-muted-foreground">{freshnessText}</div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile
            label={t("Logs 24h")}
            value={data ? <AnimatedNumber value={data.total24h} format={formatCount} /> : "—"}
            hint={data ? t("{n} gesamt", { n: data.total24h.toLocaleString(dateLocale()) }) : undefined}
            Icon={Database}
          />
          <StatTile
            label={t("Errors 24h")}
            value={data ? <AnimatedNumber value={data.errors24h} format={formatCount} /> : "—"}
            hint={
              data && data.errors24h > 0
                ? t("{n} gesamt", { n: data.errors24h.toLocaleString(dateLocale()) })
                : undefined
            }
            tone="destructive"
            Icon={CircleAlert}
            onClick={() => navigate("/logs?level=ERROR")}
          />
          <StatTile
            label={t("Warnings 24h")}
            value={data ? <AnimatedNumber value={data.warnings24h} format={formatCount} /> : "—"}
            hint={
              data && data.warnings24h > 0
                ? t("{n} gesamt", { n: data.warnings24h.toLocaleString(dateLocale()) })
                : undefined
            }
            tone="warning"
            Icon={Triangle}
            onClick={() => navigate("/logs?level=WARN")}
          />
          <StatTile
            label={t("TCP-Quellen")}
            value={data ? <AnimatedNumber value={data.sources} /> : "—"}
            Icon={Plug}
            onClick={() => navigate("/quellen/tcp")}
          />
        </div>

        <Card
          title={t("Neueste Errors")}
          description={t("Letzte 5 ERROR-Einträge aus den vergangenen 24h. Klick öffnet sie in der Logs-Ansicht.")}
          actions={
            <button
              type="button"
              onClick={() => navigate("/logs?level=ERROR")}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-xs hover:bg-muted"
            >
              {t("Alle")} <ArrowRight className="h-3 w-3" />
            </button>
          }
        >
          {!data ? (
            <div className="py-4 text-sm text-muted-foreground">{t("Lade…")}</div>
          ) : data.recentErrors.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-success">
              {t("Keine Errors in den letzten 24h.")}
            </div>
          ) : (
            <ul className="divide-y divide-border font-mono text-xs">
              {data.recentErrors.map((log) => (
                <ErrorRow key={log.id ?? log.timestamp} log={log} onJump={() => jumpToLog(navigate, log)} />
              ))}
            </ul>
          )}
        </Card>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <HealthCard
            title={t("Lizenz")}
            Icon={ShieldCheck}
            primary={license ? license.customer ?? "—" : "—"}
            secondary={
              license
                ? license.status === "full"
                  ? t("Gültig bis {date}", { date: formatDate(license.valid_until) })
                  : license.status === "grace"
                    ? t("Grace · noch {n} Tage", { n: license.days_remaining ?? "?" })
                    : license.status === "read_only"
                      ? t("Read-Only — abgelaufen")
                      : t("Keine Lizenz")
                : t("lade…")
            }
            tone={
              license?.status === "read_only" || license?.status === "unlicensed"
                ? "destructive"
                : license?.status === "grace"
                  ? "warning"
                  : "default"
            }
            onOpen={() => navigate("/einstellungen/lizenz")}
          />

          <HealthCard
            title={t("Version")}
            Icon={Download}
            primary={update ? `v${update.current}` : "—"}
            secondary={
              update
                ? update.update_available
                  ? t("Update auf v{version} verfügbar", { version: update.latest ?? "?" })
                  : update.latest
                    ? t("Aktuelle Version")
                    : t("Manifest unerreichbar")
                : t("lade…")
            }
            tone={update?.update_available ? "warning" : "default"}
            onOpen={() => navigate("/einstellungen/updates")}
          />

          <HealthCard
            title={t("Ingest-Queue")}
            Icon={HeartPulse}
            primary={
              data?.ingest
                ? t("{pct}% belegt", { pct: Math.round(data.ingest.queue_used_pct) })
                : "—"
            }
            secondary={
              data?.ingest
                ? t("{flushed} geflusht · {dropped} dropped", {
                    flushed: data.ingest.flushed.toLocaleString(dateLocale()),
                    dropped: data.ingest.dropped,
                  })
                : t("lade…")
            }
            tone={
              data?.ingest && data.ingest.queue_used_pct > 80
                ? "destructive"
                : data?.ingest && data.ingest.queue_used_pct > 50
                  ? "warning"
                  : "default"
            }
          />
        </div>
      </div>
    </div>
  );
}

function formatFreshness(lastUpdated: string | null): string {
  if (!lastUpdated) return t("Stand: lade...");
  const ts = new Date(lastUpdated);
  if (Number.isNaN(ts.getTime())) return t("Stand: unbekannt");
  const diffSec = Math.max(0, Math.round((Date.now() - ts.getTime()) / 1000));
  if (diffSec < 60) return t("Stand: vor {s}s aktualisiert", { s: diffSec });
  const diffMin = Math.round(diffSec / 60);
  return t("Stand: vor {min}min aktualisiert", { min: diffMin });
}

function jumpToLog(navigate: (to: string) => void, log: LogEntry) {
  const params = new URLSearchParams();
  params.set("level", log.level);
  if (log.client_id) params.set("client", log.client_id);
  if (log.trace_id) params.set("trace", log.trace_id);
  navigate(`/logs?${params}`);
}

function ErrorRow({ log, onJump }: { log: LogEntry; onJump: () => void }) {
  const ts = new Date(log.timestamp);
  const time = Number.isNaN(ts.getTime())
    ? log.timestamp
    : ts.toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  return (
    <li>
      <button
        type="button"
        onClick={onJump}
        // Mobil: Meta-Zeile + umbrechende Nachricht (wie LogTable); Desktop: Grid-Zeile.
        className="flex w-full cursor-pointer flex-col gap-1 px-1 py-2 text-left hover:bg-muted/40 md:grid md:grid-cols-[80px_64px_140px_1fr_auto] md:items-center md:gap-2 md:py-1.5"
      >
        <div className="flex min-w-0 items-center gap-2 md:contents">
          <span className="order-last ml-auto shrink-0 text-muted-foreground tabular-nums md:order-none md:ml-0">
            {time}
          </span>
          <LevelBadge level={log.level} />
          <span className="min-w-0 truncate text-muted-foreground">{log.client_id}</span>
        </div>
        <span className="line-clamp-2 break-words md:block md:truncate">{log.message}</span>
        <ArrowRight className="hidden h-3 w-3 text-muted-foreground md:block" />
      </button>
    </li>
  );
}

function StatTile({
  label,
  value,
  hint,
  tone = "default",
  Icon,
  onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: "default" | "destructive" | "warning";
  Icon: typeof Database;
  onClick?: () => void;
}) {
  const toneClass =
    tone === "destructive"
      ? "text-destructive"
      : tone === "warning"
        ? "text-warning"
        : "text-foreground";
  const Wrapper: "button" | "div" = onClick ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick}
      className={
        "flex flex-col gap-1 rounded-lg border border-border bg-card p-4 text-left " +
        (onClick ? "cursor-pointer transition-colors hover:bg-muted/40" : "")
      }
    >
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Icon className={"h-3 w-3 " + toneClass} />
        {label}
      </div>
      <div className={`text-2xl font-medium tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="text-[11px] text-muted-foreground">{hint}</div> : null}
    </Wrapper>
  );
}

function HealthCard({
  title,
  Icon,
  primary,
  secondary,
  tone = "default",
  onOpen,
}: {
  title: string;
  Icon: typeof Database;
  primary: string;
  secondary: string;
  tone?: "default" | "destructive" | "warning";
  onOpen?: () => void;
}) {
  const toneClass =
    tone === "destructive"
      ? "text-destructive"
      : tone === "warning"
        ? "text-warning"
        : "text-muted-foreground";
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <div className={`flex items-center gap-1.5 text-[11px] ${toneClass}`}>
          <Icon className="h-3 w-3" />
          {title}
        </div>
        {onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={t("{title} öffnen", { title })}
          >
            <ArrowRight className="h-3 w-3" />
          </button>
        ) : null}
      </div>
      <div className="text-sm font-medium">{primary}</div>
      <div className={`text-[11px] ${toneClass}`}>{secondary}</div>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(dateLocale(), { year: "numeric", month: "2-digit", day: "2-digit" });
}
